import { Injectable, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, firstValueFrom, of } from 'rxjs';
import { sanitizeTextarea, containsDangerousContent } from 'src/app/common/input-utils';
import { openExternalWindow } from 'src/app/common/window-utils';
import { CambioOrientacionRepository } from 'src/app/domain/service/cambio-orientacion/cambio-orientacion-repository';
import {
  OrdenServicioDetalleProgramar,
  ProgramacionServicio
} from 'src/app/domain/model/orden-servicio/orden-servicio';
import { OrdenServicioRepository } from 'src/app/domain/service/orden-servicio/orden-servicio-repository';
import { IdentityServiceImpl } from 'src/app/infrastructure/adapters/security/identity.service';
import { environment } from 'src/environments/environment';
import {
  IT_DETALLE_BREADCRUMB_MODULO,
  IT_DETALLE_ANULAR_RECHAZO_DETALLE_ERROR_DEFAULT,
  IT_DETALLE_ANULAR_RECHAZO_DETALLE_EXITO_DEFAULT,
  IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MAX,
  IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MIN,
  IT_DETALLE_ANULAR_RECHAZO_RESUMEN_ERROR,
  IT_DETALLE_ANULAR_RECHAZO_RESUMEN_EXITO,
  IT_DETALLE_COLSPAN_CON_MODIFICAR,
  IT_DETALLE_COLSPAN_SIN_MODIFICAR,
  IT_DETALLE_ESTADO_FACTURA_CLIENTE,
  IT_DETALLE_MENSAJE_CONFIRMAR_DETALLE_CALCULADO,
  IT_DETALLE_NAVIGATION_STATE_ID,
  IT_DETALLE_NAVIGATION_STATE_QUERY_PARAMS_REGRESO,
  IT_DETALLE_ESTADO_PENDIENTE_RECHAZO,
  IT_DETALLE_MENSAJE_BUSQUEDA_ACTA_GIROP,
  IT_DETALLE_MENSAJE_NO_PROGRAMACIONES,
  IT_DETALLE_PERMISO_ANULAR_RECHAZO_OC,
  IT_DETALLE_PERMISO_MODIFICAR_CUMPLIMIENTO,
  IT_DETALLE_RESUMEN_ADVERTENCIA,
  IT_DETALLE_RESUMEN_ERROR,
  IT_DETALLE_RESUMEN_INFORMACION_EMPRESA,
  IT_DETALLE_RESUMEN_ORDEN,
  IT_DETALLE_TOOLTIP_ANULAR_RECHAZO_SIN_PERMISO
} from './informacion-tareas-detalle.constants';

@Injectable()
export class InformacionTareasDetalleFacade {
  private readonly ordenServicioRepository = inject(OrdenServicioRepository);
  private readonly cambioOrientacionRepository = inject(CambioOrientacionRepository);
  private readonly identityService = inject(IdentityServiceImpl);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly loading = signal(false);
  readonly cargandoProgramaciones = signal(false);
  readonly detalle = signal<OrdenServicioDetalleProgramar | null>(null);
  readonly programaciones = signal<ProgramacionServicio[]>([]);
  readonly descargandoActaId = signal<number | null>(null);
  readonly permisoModificarCumplimiento = signal(false);
  readonly permisoAnularRechazo = signal(false);
  readonly mostrarModalAnularRechazo = signal(false);
  readonly guardandoAnularRechazo = signal(false);
  readonly justificacionAnularRechazo = signal('');
  readonly intentoGuardarAnularRechazo = signal(false);
  readonly mostrarModalModificar = signal(false);
  readonly guardandoModificacion = signal(false);
  readonly intentoGuardarModificar = signal(false);
  readonly programacionEnEdicion = signal<ProgramacionServicio | null>(null);
  readonly fechaProgramacionEditar = signal<Date | null>(null);
  readonly horaInicioEditar = signal<string | null>(null);
  readonly duracionEditar = signal<number | null>(null);
  readonly fechaAprobacionEditar = signal<Date | null>(null);
  readonly justificacionEditar = signal('');

  readonly titulo = IT_DETALLE_RESUMEN_ORDEN;
  readonly breadcrumbModulo = IT_DETALLE_BREADCRUMB_MODULO;
  readonly colspanTablaProgramaciones = computed(() =>
    this.permisoModificarCumplimiento()
      ? IT_DETALLE_COLSPAN_CON_MODIFICAR
      : IT_DETALLE_COLSPAN_SIN_MODIFICAR
  );
  readonly mostrarBloqueAnularRechazo = computed(
    () =>
      (this.detalle()?.estado || '').trim().toUpperCase() ===
      IT_DETALLE_ESTADO_PENDIENTE_RECHAZO
  );
  readonly habilitarAccionAnularRechazo = computed(
    () => this.mostrarBloqueAnularRechazo() && this.permisoAnularRechazo()
  );
  readonly tooltipAnularRechazo = computed(() => {
    if (this.habilitarAccionAnularRechazo()) {
      return '';
    }

    if (!this.permisoAnularRechazo()) {
      return IT_DETALLE_TOOLTIP_ANULAR_RECHAZO_SIN_PERMISO;
    }

    return '';
  });
  readonly justificacionAnularRechazoLength = computed(
    () => this.justificacionAnularRechazo().trim().length
  );

  private queryParamsRegreso: Record<string, string | number> = {};

  async init(): Promise<void> {
    const navigationState =
      this.router.getCurrentNavigation()?.extras.state ??
      (globalThis.history?.state as Record<string, unknown> | null);
    const rawId = this.route.snapshot.paramMap.get('id');
    const queryParams = this.route.snapshot.queryParams as Record<
      string,
      unknown
    >;

    const queryParamsRegresoState =
      this.extraerQueryParamsRegresoDesdeState(navigationState);

    this.queryParamsRegreso =
      Object.keys(queryParamsRegresoState).length > 0
        ? queryParamsRegresoState
        : this.mapQueryParams(queryParams);

    const id = this.resolverIdOrden(rawId, navigationState);
    if (!id) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'No se encontro un identificador valido de la orden.'
      });
      await this.regresar();
      return;
    }

    this.loading.set(true);
    try {
      await Promise.all([
        this.cargarDetalle(id),
        this.validarPermisoModificar(),
        this.validarPermisoAnularRechazo()
      ]);
    } finally {
      this.loading.set(false);
    }
  }

  async regresar(): Promise<void> {
    await this.router.navigate(['/informacion-tareas']);
  }

  async irInformacionEmpresa(): Promise<void> {
    const detalle = this.detalle();
    const resolucionBeneficiario =
      await this.resolverBeneficiarioParaInformacionEmpresa(detalle);
    const nmconsecutivoBeneficiario =
      resolucionBeneficiario.nmconsecutivoBeneficiario;
    const nombreBeneficiario = resolucionBeneficiario.nombreBeneficiario;

    if (!nmconsecutivoBeneficiario || nmconsecutivoBeneficiario <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_INFORMACION_EMPRESA,
        detail:
          'No fue posible identificar el cliente para consultar su informacion.'
      });
      return;
    }

    const queryParams: Record<string, string | number> = {
      nmconsecutivoBeneficiario
    };

    if (nombreBeneficiario) {
      queryParams['nombreBeneficiario'] = nombreBeneficiario;
    }

    await this.router.navigate(['/informacion-empresa'], { queryParams });
  }

  private async resolverBeneficiarioParaInformacionEmpresa(
    detalle: OrdenServicioDetalleProgramar | null
  ): Promise<{
    nmconsecutivoBeneficiario: number | null;
    nombreBeneficiario: string | undefined;
  }> {
    let nmconsecutivoBeneficiario = detalle?.nmconsecutivoBeneficiario ?? null;
    let nombreBeneficiario = detalle?.nombreBeneficiario;

    if (nmconsecutivoBeneficiario && nmconsecutivoBeneficiario > 0) {
      return { nmconsecutivoBeneficiario, nombreBeneficiario };
    }

    const nmconsecutivoOrden = detalle?.nmconsecutivo;
    if (!nmconsecutivoOrden || nmconsecutivoOrden <= 0) {
      return { nmconsecutivoBeneficiario: null, nombreBeneficiario };
    }

    try {
      const orden =
        await this.ordenServicioRepository.obtenerOrdenPorConsecutivo(
          nmconsecutivoOrden
        );

      nmconsecutivoBeneficiario = orden?.nmconsecutivoBeneficiario ?? null;
      nombreBeneficiario = nombreBeneficiario || orden?.nombreBeneficiario;
    } catch {
      return { nmconsecutivoBeneficiario: null, nombreBeneficiario };
    }

    return { nmconsecutivoBeneficiario, nombreBeneficiario };
  }

  async descargarActa360(programacion: ProgramacionServicio): Promise<void> {
    if (!programacion.cdconsecutivoActa && !programacion.idActa360) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: IT_DETALLE_MENSAJE_BUSQUEDA_ACTA_GIROP
      });
      return;
    }

    if (!programacion.nmcumplimientoServicio) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: IT_DETALLE_MENSAJE_BUSQUEDA_ACTA_GIROP
      });
      return;
    }

    this.descargandoActaId.set(programacion.nmconsecutivo);

    try {
      const url = `${environment.backend.baseUrl}/servicios/orientacionesProgramadas/${programacion.nmcumplimientoServicio}/descargar-acta`;
      const windowRef = openExternalWindow(url);

      if (!windowRef) {
        this.messageService.add({
          severity: 'warn',
          summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
          detail:
            'El navegador bloqueo la descarga. Habilite ventanas emergentes para continuar.'
        });
      }
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: IT_DETALLE_RESUMEN_ERROR,
        detail: 'No fue posible descargar el acta 360.'
      });
    } finally {
      this.descargandoActaId.set(null);
    }
  }

  iniciarAnularRechazo(): void {
    if (!this.habilitarAccionAnularRechazo()) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: this.tooltipAnularRechazo()
      });
      return;
    }

    this.justificacionAnularRechazo.set('');
    this.intentoGuardarAnularRechazo.set(false);
    this.mostrarModalAnularRechazo.set(true);
  }

  cerrarModalAnularRechazo(forzar = false): void {
    if (this.guardandoAnularRechazo() && !forzar) {
      return;
    }

    this.mostrarModalAnularRechazo.set(false);
    this.justificacionAnularRechazo.set('');
    this.intentoGuardarAnularRechazo.set(false);
  }

  setJustificacionAnularRechazo(value: string): void {
    this.justificacionAnularRechazo.set(value ?? '');
  }

  async confirmarAnularRechazo(): Promise<void> {
    const detalle = this.detalle();
    if (!detalle?.nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'No se encontro una orientacion valida para anular rechazo.'
      });
      return;
    }

    this.intentoGuardarAnularRechazo.set(true);

    const justificacionOriginal = this.justificacionAnularRechazo().trim();
    if (
      justificacionOriginal.length < IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MIN ||
      justificacionOriginal.length > IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MAX
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: `La justificacion debe tener entre ${IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MIN} y ${IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MAX} caracteres.`
      });
      return;
    }

    if (containsDangerousContent(this.justificacionAnularRechazo())) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La justificacion contiene caracteres no permitidos.'
      });
      return;
    }

    const justificacionLimpia = sanitizeTextarea(this.justificacionAnularRechazo())
      .slice(0, IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MAX)
      .trim();
    this.justificacionAnularRechazo.set(justificacionLimpia);

    if (!justificacionLimpia) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La justificacion es obligatoria.'
      });
      return;
    }

    if (
      justificacionLimpia.length < IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MIN ||
      justificacionLimpia.length > IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MAX
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: `La justificacion debe tener entre ${IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MIN} y ${IT_DETALLE_ANULAR_RECHAZO_JUSTIFICACION_MAX} caracteres.`
      });
      return;
    }

    this.guardandoAnularRechazo.set(true);

    try {
      const resultado =
        await this.cambioOrientacionRepository.anularRechazoOrden({
          nmconsecutivo: detalle.nmconsecutivo,
          justificacion: justificacionLimpia
        });

      if (!resultado.exito) {
        this.messageService.add({
          severity: 'error',
          summary: IT_DETALLE_ANULAR_RECHAZO_RESUMEN_ERROR,
          detail: resultado.mensaje || IT_DETALLE_ANULAR_RECHAZO_DETALLE_ERROR_DEFAULT
        });
        return;
      }

      const mensaje = resultado.mensaje || IT_DETALLE_ANULAR_RECHAZO_DETALLE_EXITO_DEFAULT;
      const esAdvertenciaNotificacion =
        mensaje.toLowerCase().includes('no fue posible enviar la notificacion');

      this.messageService.add({
        severity: esAdvertenciaNotificacion ? 'warn' : 'success',
        summary: esAdvertenciaNotificacion
          ? IT_DETALLE_RESUMEN_ADVERTENCIA
          : IT_DETALLE_ANULAR_RECHAZO_RESUMEN_EXITO,
        detail: mensaje
      });

      this.cerrarModalAnularRechazo(true);
      await this.cargarDetalle(detalle.nmconsecutivo);
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: IT_DETALLE_ANULAR_RECHAZO_RESUMEN_ERROR,
        detail: this.resolverDetalleError(error, IT_DETALLE_ANULAR_RECHAZO_DETALLE_ERROR_DEFAULT)
      });
    } finally {
      this.guardandoAnularRechazo.set(false);
    }
  }

  async iniciarModificarCumplimiento(
    programacion: ProgramacionServicio
  ): Promise<void> {
    if (!this.permisoModificarCumplimiento()) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'No cuenta con permisos para modificar el cumplimiento.'
      });
      return;
    }

    if (!this.puedeMostrarModificar(programacion)) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail:
          'La programación seleccionada no permite modificación de cumplimiento.'
      });
      return;
    }

    if (!programacion.nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail:
          'No se encontró el identificador de la programación para modificar.'
      });
      return;
    }

    this.abrirModalModificar(programacion);
  }

  cerrarModalModificarCumplimiento(): void {
    this.mostrarModalModificar.set(false);
    this.programacionEnEdicion.set(null);
    this.intentoGuardarModificar.set(false);
  }

  setFechaProgramacionEditar(value: Date | null): void {
    this.fechaProgramacionEditar.set(value);
  }

  setHoraInicioEditar(value: string | null): void {
    this.horaInicioEditar.set(this.normalizeHora(value));
  }

  setDuracionEditar(value: number | null): void {
    this.duracionEditar.set(this.toPositiveOrNull(value));
  }

  setFechaAprobacionEditar(value: Date | null): void {
    this.fechaAprobacionEditar.set(value);
  }

  setJustificacionEditar(value: string): void {
    this.justificacionEditar.set(value ?? '');
  }

  async guardarModificarCumplimiento(): Promise<void> {
    const programacion = this.programacionEnEdicion();
    if (!programacion?.nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail:
          'No se encontró la programación para actualizar el cumplimiento.'
      });
      return;
    }

    this.intentoGuardarModificar.set(true);

    const fechaProgramacion = this.fechaProgramacionEditar();
    if (!fechaProgramacion) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La fecha de programación es obligatoria.'
      });
      return;
    }

    const horaInicio = this.horaInicioEditar();
    if (!horaInicio) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La hora de inicio es obligatoria.'
      });
      return;
    }

    if (!this.esHoraValida(horaInicio)) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La hora de inicio no tiene un formato válido (HH:mm).'
      });
      return;
    }

    const duracion = this.duracionEditar();
    if (!duracion || duracion <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La duración es obligatoria y debe ser mayor a cero.'
      });
      return;
    }

    const cantidadProgramada = Number(programacion.cantidadProgramada ?? 0);
    if (cantidadProgramada > 0 && duracion > cantidadProgramada) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La duración NO debe ser mayor que la cantidad programada.'
      });
      return;
    }

    const justificacion = this.normalizeString(this.justificacionEditar());
    if (!justificacion) {
      this.messageService.add({
        severity: 'warn',
        summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
        detail: 'La justificación es obligatoria.'
      });
      return;
    }

    if (this.esEstadoFacturaCalculada(programacion.estadoFactura)) {
      const confirmarAjuste = await this.confirmarAjusteFacturaCalculada();
      if (!confirmarAjuste) {
        return;
      }
    }

    const payload = this.construirPayloadModificar(
      programacion,
      fechaProgramacion,
      horaInicio,
      duracion,
      this.fechaAprobacionEditar(),
      justificacion
    );

    this.guardandoModificacion.set(true);
    try {
      await this.ordenServicioRepository.modificarCumplimientoInformacionTareas(
        payload
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Programación modificada',
        detail: 'La modificación se registró correctamente.'
      });
      this.cerrarModalModificarCumplimiento();

      const idOrden = this.detalle()?.nmconsecutivo;
      if (idOrden) {
        await this.cargarDetalle(idOrden);
      }
    } catch (error: any) {
      const detail =
        error?.error?.message ||
        error?.error?.detail ||
        error?.message ||
        'No fue posible modificar el cumplimiento.';

      this.messageService.add({
        severity: 'error',
        summary: IT_DETALLE_RESUMEN_ERROR,
        detail
      });
    } finally {
      this.guardandoModificacion.set(false);
    }
  }

  puedeMostrarModificar(item: ProgramacionServicio): boolean {
    if (!item.nmcumplimientoServicio) {
      return false;
    }

    const estadoFactura = (item.estadoFactura || '').toUpperCase();
    if (!estadoFactura) {
      return false;
    }

    return !estadoFactura.includes(IT_DETALLE_ESTADO_FACTURA_CLIENTE);
  }

  private async cargarDetalle(nmconsecutivo: number): Promise<void> {
    this.cargandoProgramaciones.set(true);

    try {
      const [detalle, programaciones] = await Promise.all([
        this.ordenServicioRepository.obtenerDetalle(nmconsecutivo),
        this.ordenServicioRepository.consultarProgramaciones(nmconsecutivo)
      ]);

      this.detalle.set(detalle);
      this.programaciones.set(programaciones || []);

      if (!programaciones || programaciones.length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: IT_DETALLE_RESUMEN_ADVERTENCIA,
          detail: IT_DETALLE_MENSAJE_NO_PROGRAMACIONES
        });
      }
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: IT_DETALLE_RESUMEN_ERROR,
        detail: 'No fue posible cargar el detalle de la orden.'
      });
      this.detalle.set(null);
      this.programaciones.set([]);
    } finally {
      this.cargandoProgramaciones.set(false);
    }
  }

  private async validarPermisoModificar(): Promise<void> {
    const permitido = await firstValueFrom(
      this.identityService
        .isAuthorized(IT_DETALLE_PERMISO_MODIFICAR_CUMPLIMIENTO)
        .pipe(catchError(() => of(false)))
    );

    this.permisoModificarCumplimiento.set(permitido);
  }

  private async validarPermisoAnularRechazo(): Promise<void> {
    const permitido = await firstValueFrom(
      this.identityService
        .isAuthorized(IT_DETALLE_PERMISO_ANULAR_RECHAZO_OC)
        .pipe(catchError(() => of(false)))
    );

    this.permisoAnularRechazo.set(permitido);
  }

  private mapQueryParams(
    queryParams: Record<string, unknown>
  ): Record<string, string | number> {
    const result: Record<string, string | number> = {};

    Object.entries(queryParams || {}).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        result[key] = value;
      }
    });

    return result;
  }

  private extraerQueryParamsRegresoDesdeState(
    navigationState: Record<string, unknown> | null
  ): Record<string, string | number> {
    if (!navigationState) {
      return {};
    }

    const maybeQueryParamsRegreso =
      navigationState[IT_DETALLE_NAVIGATION_STATE_QUERY_PARAMS_REGRESO];

    if (
      !maybeQueryParamsRegreso ||
      typeof maybeQueryParamsRegreso !== 'object'
    ) {
      return {};
    }

    return this.mapQueryParams(
      maybeQueryParamsRegreso as Record<string, unknown>
    );
  }

  private resolverIdOrden(
    rawId: string | null,
    navigationState: Record<string, unknown> | null
  ): number | null {
    const idEnRuta = this.parseNumeroPositivo(rawId);
    if (idEnRuta) {
      return idEnRuta;
    }

    return this.parseNumeroPositivo(
      navigationState ? navigationState[IT_DETALLE_NAVIGATION_STATE_ID] : null
    );
  }

  private parseNumeroPositivo(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private abrirModalModificar(programacion: ProgramacionServicio): void {
    this.programacionEnEdicion.set(programacion);
    this.fechaProgramacionEditar.set(
      this.extraerFecha(programacion.fechaProgramacion)
    );
    this.horaInicioEditar.set(this.extraerHora(programacion.fechaProgramacion));
    this.duracionEditar.set(
      this.toPositiveOrNull(programacion.horasCita) ??
        this.toPositiveOrNull(programacion.cantidadEjecutada)
    );
    this.fechaAprobacionEditar.set(null);
    this.justificacionEditar.set('');
    this.intentoGuardarModificar.set(false);
    this.mostrarModalModificar.set(true);
  }

  private construirPayloadModificar(
    programacion: ProgramacionServicio,
    fechaProgramacionEditar: Date,
    horaInicio: string,
    duracion: number,
    fechaAprobacion: Date | null,
    justificacion: string
  ): Record<string, unknown> {
    const fechaProgramacion = this.formatearFecha(fechaProgramacionEditar);
    const fechaAprobacionFormateada = this.formatearFecha(fechaAprobacion);

    const payload: Record<string, unknown> = {
      nmconsecutivoProgramacion: programacion.nmconsecutivo,
      fechaCumplimiento: fechaAprobacionFormateada ?? fechaProgramacion,
      cantidadEjecutada: duracion,
      observacionesJustificacion: justificacion,
      observaciones: justificacion,
      cdtipoCita: programacion.nmconsecutivoTipoCita ?? null,
      duracion,
      fechaProgramacion
    };

    if (fechaAprobacionFormateada) {
      payload['fechaAprobacion'] = fechaAprobacionFormateada;
    }

    payload['horaCita'] = horaInicio;

    return payload;
  }

  private formatearFecha(date: Date | null): string | null {
    if (!date || Number.isNaN(date.getTime())) {
      return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private extraerFecha(value?: string): Date | null {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  }

  private extraerHora(value?: string): string | null {
    const date = this.extraerFecha(value);
    if (!date) {
      return null;
    }

    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    return `${horas}:${minutos}`;
  }

  private normalizeString(value: string): string | null {
    const normalized = String(value ?? '').trim();
    return normalized.length > 0 ? normalized : null;
  }

  private normalizeHora(value: string | null): string | null {
    const normalized = String(value ?? '').trim();
    return normalized.length > 0 ? normalized : null;
  }

  private toPositiveOrNull(value: number | null | undefined): number | null {
    if (value == null) {
      return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }

    return parsed;
  }

  private esHoraValida(hora: string): boolean {
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(hora);
  }

  private esEstadoFacturaCalculada(estadoFactura?: string): boolean {
    const estado = (estadoFactura || '').trim().toUpperCase();
    return estado.includes('CALCULADA') || estado === 'CALC';
  }

  private confirmarAjusteFacturaCalculada(): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'confirmarModificarProgramacion',
        header: 'Confirmar modificación de cumplimiento',
            message: IT_DETALLE_MENSAJE_CONFIRMAR_DETALLE_CALCULADO,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Continuar',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-primary',
        rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });
  }

  private resolverDetalleError(error: any, fallback: string): string {
    return (
      error?.error?.message ||
      error?.error?.detail ||
      error?.message ||
      fallback
    );
  }
}
